import { useState, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ThumbsUp, MessageCircle, AlertTriangle, Send, Loader2 } from 'lucide-react';
import type { FeedbackSentiment } from '../../types';
import { useFeedback } from '../../context';
import { getSectionLabel } from '../../utils';

const sentimentOptions: {
  value: FeedbackSentiment;
  label: string;
  icon: typeof ThumbsUp;
  color: string;
  bgColor: string;
}[] = [
  {
    value: 'positive',
    label: 'Looks Good',
    icon: ThumbsUp,
    color: 'text-green-600',
    bgColor: 'bg-green-50 border-green-200 hover:bg-green-100',
  },
  {
    value: 'neutral',
    label: 'Question',
    icon: MessageCircle,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
  },
  {
    value: 'concern',
    label: 'Concern',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
  },
];

export function FeedbackModal() {
  const {
    activeSection,
    activeLine,
    isSubmitting,
    closeFeedbackModal,
    submitFeedback,
  } = useFeedback();

  const [sentiment, setSentiment] = useState<FeedbackSentiment>('neutral');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const isOpen = activeSection !== null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!comment.trim()) {
      setError('Please enter a comment');
      return;
    }

    if (!activeSection) return;

    try {
      await submitFeedback({
        section: activeSection,
        lineNumber: activeLine ?? undefined,
        comment: comment.trim(),
        sentiment,
      });

      // Reset form
      setComment('');
      setSentiment('neutral');
    } catch {
      setError('Failed to submit feedback. Please try again.');
    }
  };

  const handleClose = () => {
    setComment('');
    setSentiment('neutral');
    setError('');
    closeFeedbackModal();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-x-4 top-[65%] -translate-y-1/2 z-50 max-w-lg mx-auto"
            initial={{ opacity: 0, scale: 0.95, y: '-45%' }}
            animate={{ opacity: 1, scale: 1, y: '-50%' }}
            exit={{ opacity: 0, scale: 0.95, y: '-45%' }}
            transition={{ duration: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-hul-navy px-6 py-5 relative">
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <h2 className="font-display text-2xl text-white tracking-wide">
                  Share Feedback
                </h2>
                <p className="text-white/70 mt-1">
                  {activeSection && getSectionLabel(activeSection)}
                  {activeLine && <span className="ml-2">· Line #{activeLine}</span>}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6">
                {/* Sentiment Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    How do you feel about this?
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {sentimentOptions.map((option) => {
                      const Icon = option.icon;
                      const isSelected = sentiment === option.value;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setSentiment(option.value)}
                          className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl transition-all ${
                            isSelected
                              ? `${option.bgColor} border-current ${option.color}`
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Icon
                            className={`w-6 h-6 ${isSelected ? option.color : 'text-gray-400'}`}
                          />
                          <span
                            className={`text-sm font-medium ${
                              isSelected ? option.color : 'text-gray-600'
                            }`}
                          >
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Comment */}
                <div className="mb-6">
                  <label
                    htmlFor="comment"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Your Comment
                  </label>
                  <textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-hul-navy focus:ring-0 transition-colors resize-none"
                    placeholder={
                      sentiment === 'positive'
                        ? 'What do you like about this?'
                        : sentiment === 'concern'
                        ? 'What concerns do you have?'
                        : 'What would you like to know?'
                    }
                  />
                </div>

                {/* Error */}
                {error && (
                  <div className="mb-4 px-4 py-3 bg-red-50 text-red-600 text-sm rounded-lg">
                    {error}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !comment.trim()}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-hul-red text-white font-medium rounded-xl hover:bg-hul-red-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Feedback
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
